import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  OAuthProvider } from "firebase/auth";


import { collection, doc, serverTimestamp, setDoc } from "firebase/firestore";
import { auth, db } from "../firebaseConfig";
import { useEntitlements } from "../hooks/useEntitlements";
import { getToken } from "firebase/app-check";
import { appCheck } from "../firebaseConfig"; // exporta appCheck desde tu config

import "../styles/generalStyle.css";
import "./CreateSongIdeas.css"; // ✅ Reutiliza tu CSS (chips, summary, note, etc.)

import { useLanguage } from "../i18n/LanguageContext";

const GENERAR_LETRA_URL_ANTES = "https://generarletra-eoq6iazhiq-uc.a.run.app";
const GENERAR_LETRA_URL = "https://us-central1-cancionesia.cloudfunctions.net/generarLetraCheck";

const DRAFT_KEY = "songWizardDraft_v1";

const GUEST_LETRAS_LIMIT = 3;         // 2 o 3, como tú decidas
const GUEST_COOLDOWN_MS = 600_000;     // 10 min entre generaciones (anti spam)
const GUEST_KEY = "guestLyricsLimits_v1";

function loadGuestLimits() {
  try {
    const raw = sessionStorage.getItem(GUEST_KEY);
    return raw ? JSON.parse(raw) : { left: GUEST_LETRAS_LIMIT, lastAt: 0 };
  } catch {
    return { left: GUEST_LETRAS_LIMIT, lastAt: 0 };
  }
}

function saveGuestLimits(v) {
  try { sessionStorage.setItem(GUEST_KEY, JSON.stringify(v)); } catch {}
}

function resetGuestLimits() {
  try { sessionStorage.removeItem(GUEST_KEY); } catch {}
}


function loadDraft() {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
function saveDraft(draft) {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch {}
}
function clearDraft() {
  try {
    sessionStorage.removeItem(DRAFT_KEY);
  } catch {}
}

const defaultDraft = {
  step: 0,

  // step 1
  estilo: "",
  instrumento: "",
  voz: "hombre",

  // step 2
  ocasion: "",

  // step 3
  animo: "",

  // step 4
  fechas: "",
  eventos: "",

  // step 5
  nombres: "",

  // step 6
  historia: "",

  // step 7
  extras: "",

  // step 8
  letra: "",
  prompt: "",
  generatedAt: null
};

export default function SongWizard() {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();

  const [firebaseUser, setFirebaseUser] = useState(() => auth.currentUser);
  const [draft, setDraft] = useState(() => loadDraft() || defaultDraft);

  const {
    loading: entLoading,
    canSong,
    consumeSongCredit,
    canLyrics,
    consumeLyricsCredit,
    entitlements
  } = useEntitlements(firebaseUser?.uid);

  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // auth embed
  const [authTab, setAuthTab] = useState("login"); // login | signup
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [nombre, setNombre] = useState("");
  const [authError, setAuthError] = useState("");
  const [authBusy, setAuthBusy] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [createdId, setCreatedId] = useState(null);
  const [guestLimits, setGuestLimits] = useState(() => loadGuestLimits());
  useEffect(() => {
    saveGuestLimits(guestLimits);
  }, [guestLimits]);


  // session listener
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setFirebaseUser(u || null));
    return () => unsub();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await getRedirectResult(auth);

        // Si NO venimos de Apple, res será null y no pasa nada
        if (!res?.user) return;

        const fullName = res?._tokenResponse?.fullName;

        await upsertUsuario({ user: res.user, fullName });

        // Si quieres que al loguearse avance/continúe el wizard:
        // await finalizeCreateSong(res.user);

      } catch (err) {
        // Si no hay redirect pendiente, puede dar error leve o null; solo mostramos si trae code real
        if (err?.code) {
          console.error("Apple redirect result error:", err);
          setAuthError(mapAuthErrorWizard(err));
        }
      } finally {
        setAuthBusy(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // autosave draft
  const saveTimerRef = useRef(null);

    useEffect(() => {
    // Debounce: evita escribir en sessionStorage en cada tecla
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

    saveTimerRef.current = setTimeout(() => {
        saveDraft(draft);
    }, 350); // 300–600ms va perfecto

    return () => {
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
    }, [draft]);


  const totalSteps = 9; // 0..8

  const ideas = useMemo(() => {
    // ✅ Construye la variable ideas (todo el formulario)
    const lines = [
      `Estilo musical: ${draft.estilo?.trim() || "-"}`,
      draft.instrumento?.trim() ? `Instrumento predominante: ${draft.instrumento.trim()}` : `Instrumento predominante: -`,
      `Voz: ${draft.voz === "mujer" ? "femenina" : "masculina"}`,
      "",
      `Ocasión: ${draft.ocasion?.trim() || "-"}`,
      `Estado de ánimo: ${draft.animo?.trim() || "-"}`,
      "",
      `Fechas importantes:`,
      `${draft.fechas?.trim() || "-"}`,
      "",
      `Eventos:`,
      `${draft.eventos?.trim() || "-"}`,
      "",
      `Nombres a mencionar (opcional):`,
      `${draft.nombres?.trim() || "-"}`,
      "",
      `Historia / vivencia:`,
      `${draft.historia?.trim() || "-"}`,
      "",
      `Detalles finales (gustos / frase / lugar):`,
      `${draft.extras?.trim() || "-"}`
    ];
    return lines.join("\n");
  }, [draft]);

  const promptDefault = useMemo(() => {
    const parts = [
      `Estilo: ${draft.estilo?.trim() || "-"}`,
      draft.instrumento?.trim() ? `Instrumentos: ${draft.instrumento.trim()}` : null,
      `Voz: ${draft.voz === "mujer" ? "femenina" : "masculina"}`
    ].filter(Boolean);
    return parts.join(" | ");
  }, [draft.estilo, draft.instrumento, draft.voz]);

  const step = draft.step;

  const setField = (key, value) => setDraft((d) => ({ ...d, [key]: value }));
  const goNext = () => setDraft((d) => ({ ...d, step: Math.min(d.step + 1, totalSteps - 1) }));
  const goBack = () => setDraft((d) => ({ ...d, step: Math.max(d.step - 1, 0) }));

  const validateStep = () => {
    setError("");

    // Reglas mínimas (sin hacer pesado):
    // - En step 1 requiere estilo
    if (step === 1 && !draft.estilo.trim()) return t("songWizard.errors.requiredStyle");

    // - Antes de generar letra (step 8) requiere ideas >= 100 chars
    if (step === 7) {
      const len = ideas.trim().length;
      if (len < 100) return t("songWizard.errors.minChars");
    }
    return "";
  };

  const handleNext = () => {
    const msg = validateStep();
    if (msg) {
      setError(msg);
      return;
    }
    goNext();
  };

  const isGuest = !firebaseUser;
  const guestLeft = guestLimits.left || 0;
  const msSinceLast = Date.now() - (guestLimits.lastAt || 0);
  const guestInCooldown = msSinceLast < GUEST_COOLDOWN_MS;

  const canGenerateAsGuest = guestLeft > 0 && !guestInCooldown;
  const alreadyGenerated = !!draft.generatedAt; // o draft.letra.trim().length > 0

  const mapAuthErrorWizard = (err) => {
    const code = err?.code || "";

    // Comunes
    if (code === "auth/invalid-credential") return t("auth.errors.invalidCredential");
    if (code === "auth/user-not-found") return t("auth.errors.userNotFound");
    if (code === "auth/wrong-password") return t("auth.errors.wrongPassword");
    if (code === "auth/too-many-requests") return t("auth.errors.tooManyRequests");
    if (code === "auth/popup-closed-by-user") return t("auth.errors.popupClosed");
    if (code === "auth/cancelled-popup-request") return t("auth.errors.popupCancelled");
    if (code === "auth/popup-blocked") return t("auth.errors.popupBlocked");
    if (code === "auth/account-exists-with-different-credential") return t("auth.errors.diffCredential");
    if (code === "auth/operation-not-allowed") return t("auth.errors.notEnabled");
    if (code === "auth/network-request-failed") return t("auth.errors.network");

    return t("auth.errors.generic");
  };


  const handleGenerateLyrics = async () => {
    // 1) ya generó -> bloquear (evita spam)
    if (draft.letra?.trim()) {
      setError(t("songWizard.errors.alreadyGenerated"));
      return;
    }

    // 2) validación: ideas >= 100
    const len = ideas.trim().length;
    if (len < 100) {
      setError(t("songWizard.errors.minChars"));
      return;
    }

    // 3) si NO está logueado, aplicar límites anti-bot
    if (!firebaseUser) {
      if (guestLeft <= 0) {
        setError(t("songWizard.errors.guestLimit"));
        return;
      }
      if (guestInCooldown) {
        const secs = Math.ceil((GUEST_COOLDOWN_MS - msSinceLast) / 1000);
        setError(t("songWizard.errors.cooldown", { seconds: secs }));
        return;
      }
    }else{
      if (!canLyrics) {
        setError(t("limites.limiteAlcanzadoLetra"));
        return;
      }
    }

   

    try {
      setBusy(true);
      setError("");
      setCreatedId(null);

       // ✅ 1) App Check token
      const { token } = await getToken(appCheck, false);
      

      if (firebaseUser) {
        await consumeLyricsCredit();
      }

       // ✅ 3) Un solo fetch (con App Check)
      const resp = await fetch(GENERAR_LETRA_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Firebase-AppCheck": token,
        },
        body: JSON.stringify({ idea: ideas,  targetLang: lang  }),
      });
      



      let data = {};
      try { data = await resp.json(); } catch {}

      if (!resp.ok) {
        throw new Error(data?.error || t("songWizard.errors.generic"));
      }

      const letra = (data?.letra || "").trim();

      // ✅ si es invitado, consumir 1 “intento”
      if (!firebaseUser) {
        setGuestLimits((prev) => ({
          left: Math.max((prev.left || 0) - 1, 0),
          lastAt: Date.now()
        }));
      }

      setDraft((d) => ({
        ...d,
        letra,
        prompt: d.prompt?.trim() ? d.prompt : promptDefault,
        generatedAt: Date.now(),
        step: 8
      }));
    } catch (e) {
      console.error("generateLyrics error:", e);
      if (String(e?.message || "").includes("NO_SONG_CREDITS")) {
        setError("Ya llegaste al límite. Realiza un pago para crear más letras.");
      } else {
        setError(t("createSong.errors.generic"));
      }
    } finally {
      setBusy(false);
    }
  };


  const copyLyrics = async () => {
    try {
      await navigator.clipboard.writeText(draft.letra || "");
      // mini feedback
      setError(""); // limpiamos
      // podrías poner un toast; aquí simple:
      alert(t("songWizard.copied"));
    } catch {
      setError(t("songWizard.errors.generic"));
    }
  };

  const finalizeCreateSong = async (user) => {
    // user: firebase user
    if (!user) {
      setError(t("songWizard.errors.notLogged"));
      return;
    }
    if (!draft.letra.trim()) {
      setError(t("songWizard.errors.requiredLyrics"));
      return;
    }
    if (!draft.estilo.trim()) {
      setError(t("songWizard.errors.requiredStyle"));
      return;
    }

    if (!canSong) {
      setError(t("limites.limiteAlcanzadoCancion"));
      return;
    }

    try {
      setBusy(true);
      setError("");

      await consumeSongCredit();

    
      const cancionesCol = collection(db, "usuarios", user.uid, "canciones");
      const docRef = doc(cancionesCol);

       if (window.fbq) {
        window.fbq("track", "Lead", {
          content_name: "cancion_solicitada",
          method: "pedido_cancion",
          language: lang,  
          song_id: docRef.id,               
        });
      }

      const data = {
        id: docRef.id,
        userId: user.uid,
        titulo: t("song.noTitle", "Untitled"),
        letra: draft.letra,

        estilo: draft.estilo.trim(),
        fechaCreacion: serverTimestamp(),

        pago: false,
        prompt: (draft.prompt?.trim() || promptDefault),
        status: "pending"
      };

      await setDoc(docRef, data, { merge: true });

        // ✅ Crear/actualizar doc mini para compartir (cancionesWeb/{songId})
      const miniRef = doc(db, "cancionesWeb", docRef.id);
      const miniData = {
        songId: docRef.id,
        usuarioId: firebaseUser.uid,
        shareLang: "", 

        coverUrl: "",
        coverUrl02: "",
        videoUrl: "",
        videoUrl02: "",
        desdeWeb: true,

        status: "pending",

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

       await setDoc(miniRef, miniData, { merge: true });

     // setCreatedId(docRef.id);
      clearDraft();
      navigate("/app/songs");
    } catch (e) {
      console.error("finalizeCreateSong error:", e);
      //  ✅ si falló por créditos
      if (String(e?.message || "").includes("NO_SONG_CREDITS")) {
        setError("Ya llegaste al límite. Realiza un pago para crear más canciones.");
      } else {
        setError(t("createSong.errors.generic"));
      }
    } finally {
      setBusy(false);
    }
  };

  const buildTitulo = () => {
    // Si no quieres pedir título en wizard, esto lo genera.
    // Pero tú sí quieres título final? (lo puedes añadir en step 8 si gustas)
    // Por ahora: título basado en ocasión + estilo.
    const base = draft.ocasion?.trim()
      ? `${draft.ocasion.trim()} - ${draft.estilo.trim() || "Canción"}`
      : draft.estilo.trim()
      ? `Canción ${draft.estilo.trim()}`
      : "Canción personalizada";
    return base.length > 60 ? base.slice(0, 60) : base;
  };

  // EMBED AUTH
  const onLogin = async () => {
    try {
      setAuthBusy(true);
      setAuthError("");

      const cred = await signInWithEmailAndPassword(auth, email.trim(), pass);

      if (window.fbq) {
        window.fbq("track", "CompleteRegistration", {
          method: "email_formulario", // o "google"/"apple"
        });
      }

     // await finalizeCreateSong(cred.user);
    } catch (e) {
      console.error("login error:", e);
      setAuthError(t("songWizard.auth.errors.loginFailed"));
    } finally {
      setAuthBusy(false);
    }
  };

  const onSignup = async () => {
    try {
      setAuthBusy(true);
      setAuthError("");

      if (!nombre.trim()) {
        setAuthError(t("songWizard.auth.errors.nameRequired"));
        return;
      }

      const cred = await createUserWithEmailAndPassword(auth, email.trim(), pass);

      // crea doc usuario (opcional pero recomendable)
      const userRef = doc(db, "usuarios", cred.user.uid);
      await setDoc(
        userRef,
        {
          userId: cred.user.uid,
          nombre: nombre.trim(),
          correo: email.trim(),
          createdAt: serverTimestamp()
        },
        { merge: true }
      );

     

      //await finalizeCreateSong(cred.user);
    } catch (e) {
      console.error("signup error:", e);
      setAuthError(t("songWizard.auth.errors.signupFailed"));
    } finally {
      setAuthBusy(false);
    }
  };

  const onGoogleLogin = async () => {
  try {
    setAuthBusy(true);
    setAuthError("");

    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });

    const result = await signInWithPopup(auth, provider);

    
    if (window.fbq) {
      window.fbq("track", "CompleteRegistration", {
        method: "google_formulario", // o "google"/"apple"
      });
    }

    await upsertUsuario({ user: result.user });


    // 👉 aquí continúas tu wizard
    // await finalizeCreateSong(result.user);

  } catch (err) {
    console.error("Google login error:", err);
    setAuthError(mapAuthErrorWizard(err));
  } finally {
    setAuthBusy(false);
  }
};



 const upsertUsuario = async ({ user, fullName }) => {
  if (!user?.uid) return;

  const nombreApple =
    fullName?.givenName || fullName?.familyName
      ? `${fullName?.givenName || ""} ${fullName?.familyName || ""}`.trim()
      : "";

  const userData = {
    userId: user.uid,
    nombre: (nombreApple || user.displayName || "").trim(),
    correo: (user.email || "").trim(),
    fotoPerfil: (user.photoURL || "").trim(),
    createdAt: serverTimestamp(),
    fechaRegistro: serverTimestamp()
  };

  const ref = doc(db, "usuarios", user.uid);
  await setDoc(ref, userData, { merge: true });
};


 const onAppleLogin = async () => {
  try {
    setAuthBusy(true);
    setAuthError("");

    const provider = new OAuthProvider("apple.com");
    provider.addScope("email");
    provider.addScope("name");

    if (window.fbq) {
      window.fbq("track", "CompleteRegistration", {
        method: "apple_formulario", // o "google"/"apple"
      });
    }


    await signInWithRedirect(auth, provider);
    // OJO: aquí NO sigue el código, porque redirige
  } catch (err) {
    console.error("Apple login start error:", err);
    setAuthError(mapAuthErrorWizard(err));
    setAuthBusy(false);
  }
};


  // SUCCESS screen after saving song
  if (createdId) {
    return (
      <div className="page">
        <div className="container">
          <div className="card">
            <h1 className="h1">{t("songWizard.savedTitle")}</h1>
            <p className="p-muted" style={{ marginTop: 8 }}>
              {t("songWizard.savedText")}
            </p>

            <div className="createdBox">
              <div className="createdRow">
                <span className="createdLabel">{t("songWizard.savedIdLabel")}</span>
                <span className="createdValue">{createdId}</span>
              </div>
              <div className="createdRow">
                <span className="createdLabel">{t("songWizard.statusLabel")}</span>
                <span className="createdValue">pending</span>
              </div>
            </div>

            <button
              className="btn btn-primary"
              style={{ marginTop: 14 }}
              onClick={() => navigate("/app/songs")}
            >
              {t("songWizard.goToSongs")}
            </button>

     
          </div>
        </div>
      </div>
    );
  }

  // UI blocks per step
  const StepHeader = ({ title, text }) => (
    <>
      <h1 className="h1">{title}</h1>
      {text ? (
        <p className="p-muted" style={{ marginTop: 8 }}>
          {text}
        </p>
      ) : null}
      <p className="p-muted" style={{ marginTop: 8, fontSize: 12 }}>
        {t("songWizard.step", { current: step + 1, total: totalSteps })}
      </p>
    </>
  );

  const NavButtons = ({ showBack = true, showNext = true, nextLabel }) => (
    <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
      {showBack ? (
        <button className="btn" type="button" onClick={goBack} disabled={busy || authBusy}>
          {t("songWizard.back")}
        </button>
      ) : null}

      {showNext ? (
        <button
          className="btn btn-primary"
          type="button"
          onClick={handleNext}
          disabled={busy || authBusy}
        >
          {nextLabel || t("songWizard.next")}
        </button>
      ) : null}
    </div>
  );


  const basePrice =
    lang === "pt" ? "R$ 60" :
    lang === "fr" ? "15 €" :
    lang === "de" ? "15 €" :
    "USD$ 15"; // es + en por defecto



 
  const renderWizardCard  = () => {
   if (step === 0) {
      return (
        <section className="card">
          <StepHeader title={t("songWizard.step0Title")} />

          <div className="step0Text">
            <p>{t("songWizard.step0P1")}</p>
            <p>{t("songWizard.step0P2")}</p>

            <p>
              <strong>{t("songWizard.step0Bold")}</strong>{" "}
              {t("songWizard.step0PriceLine", { basePrice })}
            </p>

            <p>{t("songWizard.step0P4")}</p>
          </div>

          <button
            className="btn btn-primary"
            style={{ marginTop: 14 }}
            onClick={goNext}
          >
            {t("songWizard.start")}
          </button>
        </section>
      );
    }


    if (step === 1) {
      return (
        <section className="card">
          <StepHeader title={t("songWizard.step1Title")} />
          <div className="form" style={{ marginTop: 10 }}>
            <label className="label">
              {t("songWizard.styleLabel")} *
              <input
                className="input"
                value={draft.estilo}
                onChange={(e) => setField("estilo", e.target.value)}
                placeholder={t("songWizard.stylePlaceholder")}
                maxLength={80}
              />
            </label>

            <label className="label">
              {t("songWizard.instrumentLabel")}
              <input
                className="input"
                value={draft.instrumento}
                onChange={(e) => setField("instrumento", e.target.value)}
                placeholder={t("songWizard.instrumentPlaceholder")}
                maxLength={80}
              />
            </label>

            <div className="label">
              {t("songWizard.voiceLabel")}
              <div className="voiceRow">
                <button
                  type="button"
                  className={`voiceChip ${draft.voz === "hombre" ? "active" : ""}`}
                  onClick={() => setField("voz", "hombre")}
                >
                  {t("songWizard.voiceMale")}
                </button>
                <button
                  type="button"
                  className={`voiceChip ${draft.voz === "mujer" ? "active" : ""}`}
                  onClick={() => setField("voz", "mujer")}
                >
                  {t("songWizard.voiceFemale")}
                </button>
              </div>
            </div>

            {error ? <div className="alert-error">{error}</div> : null}
            <NavButtons showBack={true} showNext={true} />
          </div>
        </section>
      );
    }

    if (step === 2) {
      return (
        <section className="card">
          <StepHeader title={t("songWizard.step2Title")} />
          <div className="form" style={{ marginTop: 10 }}>
            <label className="label">
              {t("songWizard.occasionLabel")}
              <input
                className="input"
                value={draft.ocasion}
                onChange={(e) => setField("ocasion", e.target.value)}
                placeholder={t("songWizard.occasionPlaceholder")}
                maxLength={100}
              />
            </label>
            {error ? <div className="alert-error">{error}</div> : null}
            <NavButtons />
          </div>
        </section>
      );
    }

    if (step === 3) {
      return (
        <section className="card">
          <StepHeader title={t("songWizard.step3Title")} />
          <div className="form" style={{ marginTop: 10 }}>
            <label className="label">
              {t("songWizard.moodLabel")}
              <input
                className="input"
                value={draft.animo}
                onChange={(e) => setField("animo", e.target.value)}
                placeholder={t("songWizard.moodPlaceholder")}
                maxLength={100}
              />
            </label>
            <NavButtons />
          </div>
        </section>
      );
    }

    if (step === 4) {
      return (
        <section className="card">
          <StepHeader title={t("songWizard.step4Title")} />
          <div className="form" style={{ marginTop: 10 }}>
            <label className="label">
              {t("songWizard.datesLabel")}
              <textarea
                className="input textarea"
                value={draft.fechas}
                onChange={(e) => setField("fechas", e.target.value)}
                placeholder={t("songWizard.datesPlaceholder")}
                rows={6}
              />
            </label>
            <label className="label">
              {t("songWizard.datesExtraLabel")}
              <textarea
                className="input textarea"
                value={draft.eventos}
                onChange={(e) => setField("eventos", e.target.value)}
                placeholder={t("songWizard.datesExtraPlaceholder")}
                rows={3}
              />
            </label>
            <NavButtons />
          </div>
        </section>
      );
    }

    if (step === 5) {
      return (
        <section className="card">
          <StepHeader title={t("songWizard.step5Title")} />
          <div className="form" style={{ marginTop: 10 }}>
            <label className="label">
              {t("songWizard.namesLabel")}
              <textarea
                className="input textarea"
                value={draft.nombres}
                onChange={(e) => setField("nombres", e.target.value)}
                placeholder={t("songWizard.namesPlaceholder")}
                rows={5}
              />
              <span className="help-text">{t("songWizard.namesHelp")}</span>
            </label>
            <NavButtons />
          </div>
        </section>
      );
    }

    if (step === 6) {
      return (
        <section className="card">
          <StepHeader title={t("songWizard.step6Title")} />
          <div className="form" style={{ marginTop: 10 }}>
            <label className="label">
              {t("songWizard.storyLabel")}
              <textarea
                className="input textarea"
                value={draft.historia}
                onChange={(e) => setField("historia", e.target.value)}
                placeholder={t("songWizard.storyPlaceholder")}
                rows={7}
              />
            </label>
            <NavButtons />
          </div>
        </section>
      );
    }

    if (step === 7) {
      return (
        <section className="card">
          <StepHeader title={t("songWizard.step7Title")} />
          <div className="form" style={{ marginTop: 10 }}>
            <label className="label">
              {t("songWizard.extraLabel")}
              <textarea
                className="input textarea"
                value={draft.extras}
                onChange={(e) => setField("extras", e.target.value)}
                placeholder={t("songWizard.extraPlaceholder")}
                rows={6}
              />
            </label>

            {error ? <div className="alert-error">{error}</div> : null}

            <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
              <button className="btn" type="button" onClick={goBack} disabled={busy}>
                {t("songWizard.back")}
              </button>

              <button
                className="btn btn-primary"
                type="button"
                onClick={handleGenerateLyrics}
                disabled={
                  busy ||  entLoading ||  (firebaseUser && !canLyrics) ||
                  !!draft.letra.trim() ||                 // ✅ ya generó, bloquea
                  (!firebaseUser && !canGenerateAsGuest)  // ✅ invitado sin intentos/cooldown
                }
              >
                {busy ? t("songWizard.generatingLyrics") : t("songWizard.generateLyrics")}
              </button>
            </div>

            {!firebaseUser ? (
              <p className="p-muted" style={{ marginTop: 8, fontSize: 12 }}>
                {t("songWizard.guestLeft", { count: guestLeft })}
              </p>
            ) : null}

            {firebaseUser && !entLoading && !canLyrics ? (
              <div className="alert-error" style={{ marginTop: 10 }}>
                {t("limites.limiteAlcanzadoLetra")}
              </div>
            ) : null}


            {busy ? (
              <div style={{ marginTop: 14, display: "flex", gap: 10, alignItems: "center" }}>
                <div className="spinner" />
                <p style={{ margin: 0 }} className="p-muted">
                  {t("songWizard.generatingText")}
                </p>
              </div>
            ) : null}
          </div>
        </section>
      );
    }

    // step 8 (lyrics + prompt + create song + embedded auth)
    return (
      <section className="card">
        <StepHeader title={t("songWizard.step8Title")} />

        {error ? <div className="alert-error" style={{ marginTop: 10 }}>{error}</div> : null}

        <div className="form" style={{ marginTop: 10 }}>
          <label className="label">
            {t("songWizard.lyricsLabel")}
            <textarea
              className="input textarea"
              value={draft.letra}
              onChange={(e) => setField("letra", e.target.value)}
              placeholder={t("songWizard.lyricsPlaceholder")}
              rows={10}
            />
          </label>         

          <label className="label" style={{ marginTop: 12 }}>
            {t("songWizard.promptLabel")}
            <textarea
              className="input textarea"
              value={draft.prompt}
              onChange={(e) => setField("prompt", e.target.value)}
              placeholder={promptDefault}
              rows={2}
            />
            <span className="help-text">{t("songWizard.promptHelp")}</span>
          </label>

           <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="btn" type="button" onClick={copyLyrics} disabled={!draft.letra.trim()}>
              {t("songWizard.copyLyrics")}
            </button>
            <button className="btn" type="button" onClick={goBack} disabled={busy || authBusy}>
              {t("songWizard.back")}
            </button>
          </div>

          <p className="note">{t("songWizard.aiNote")}</p>

          {/* If logged in -> create song */}
          {firebaseUser ? (
            <>
            <div
              style={{
                marginTop: 6,
                marginBottom: 6,
                fontWeight: 700,
                color: "#2563eb" // azul tipo "blue-600"
              }}
            >
              {t("songWizard.inicioSesionCorrecto")}
            </div>
              <button
                className="btn btn-primary"
                type="button"
                onClick={() => finalizeCreateSong(firebaseUser)}
                disabled={busy || entLoading || !canSong}
                style={{ marginTop: 10 }}
              >
                {busy ? t("songWizard.creatingSong") : t("songWizard.createSong")}
              </button>
              
              {!entLoading && !canSong ? (
                  <div className="alert-error" style={{ marginTop: 10 }}>
                    {t("limites.limiteAlcanzadoCancion")}
                    
                  </div>
                ) : null}
              </>
          ) : (
            <div className="hintBox" style={{ marginTop: 12 }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>{t("songWizard.authTitle")}</h3>
              <p className="hintText">{t("songWizard.authText")}</p>

              {/* tabs */}
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  type="button"
                  className={`voiceChip ${authTab === "login" ? "active" : ""}`}
                  onClick={() => { setAuthTab("login"); setAuthError(""); }}
                >
                  {t("songWizard.login")}
                </button>
                <button
                  type="button"
                  className={`voiceChip ${authTab === "signup" ? "active" : ""}`}
                  onClick={() => { setAuthTab("signup"); setAuthError(""); }}
                >
                  {t("songWizard.signup")}
                </button>
              </div>

              <div style={{ marginTop: 10 }}>
                {authTab === "signup" ? (
                  <label className="label">
                    {t("songWizard.auth.nameLabel")}
                    <input
                      className="input"
                      value={nombre}
                      onChange={(e) => setNombre(e.target.value)}
                      placeholder={t("songWizard.auth.namePlaceholder")}
                    />
                  </label>
                ) : null}

                <label className="label">
                  {t("songWizard.auth.emailLabel")}
                  <input
                    className="input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("songWizard.auth.emailPlaceholder")}
                  />
                </label>

                <label className="label">
                  {t("songWizard.auth.passLabel")}
                  <input
                    className="input"
                    type="password"
                    value={pass}
                    onChange={(e) => setPass(e.target.value)}
                    placeholder={t("songWizard.auth.passPlaceholder")}
                  />
                </label>

                {authError ? <div className="alert-error" style={{ marginTop: 8 }}>{authError}</div> : null}

                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={authTab === "login" ? onLogin : onSignup}
                  disabled={authBusy || busy}
                  style={{ marginTop: 10 }}
                >
                  {authBusy
                    ? t("songWizard.auth.working")
                    : authTab === "login"
                    ? t("songWizard.auth.loginButton")
                    : t("songWizard.auth.signupButton")}
                </button>
               <div className="divider" />

                <div className="social-divider">
                  <span>{t("auth.login.orContinue")}</span>
                </div>

                <button
                  type="button"
                  className="social-btn google"
                  onClick={onGoogleLogin}
                  disabled={authBusy || busy}
                >
                  <span className="social-icon">G</span>
                  {t("auth.login.google")}
                </button>

                <button
                  type="button"
                  className="social-btn apple"
                  onClick={onAppleLogin}
                   disabled={authBusy || busy}
                >
                  <span className="social-icon"></span>
                  {t("auth.login.apple")}
                </button>

              </div>
            </div>
          )}
        </div>
      </section>
    );
  };

  const renderSummaryCard  = () => {
    return (
      <section className="card">
        <h2 className="h2">{t("songWizard.summaryTitle")}</h2>
        <p className="p-muted">{t("songWizard.summarySubtitle")}</p>

        <div className="summaryBox">
          <div className="summaryRow">
            <span className="summaryLabel">{t("songWizard.summaryStyle")}</span>
            <span className="summaryValue">{draft.estilo.trim() || "--"}</span>
          </div>
          <div className="summaryRow">
            <span className="summaryLabel">{t("songWizard.summaryInstrument")}</span>
            <span className="summaryValue">{draft.instrumento.trim() || "--"}</span>
          </div>
          <div className="summaryRow">
            <span className="summaryLabel">{t("songWizard.summaryVoice")}</span>
            <span className="summaryValue">{draft.voz === "mujer" ? t("songWizard.voiceFemale") : t("songWizard.voiceMale")}</span>
          </div>
          <div className="summaryRow">
            <span className="summaryLabel">{t("songWizard.summaryIdeas")}</span>
            <span className="summaryValue">{ideas.trim() ? "✅" : "--"}</span>
          </div>
          <div className="summaryRow">
            <span className="summaryLabel">{t("songWizard.summaryLyrics")}</span>
            <span className="summaryValue">{draft.letra.trim() ? "✅" : "--"}</span>
          </div>
        </div>

        <div className="summaryPrompt">
          <span className="summaryLabel">{t("songWizard.summaryPrompt")}</span>
          <div className="promptBox">{draft.prompt?.trim() || promptDefault}</div>
        </div>

        <div style={{ marginTop: 12 }}>
          <button
            className="btn"
            type="button"
            onClick={() => {
              setDraft(defaultDraft);
              clearDraft();
              setError("");
            }}
          >
            {t("songWizard.reset")}
          </button>
        </div>
      </section>
    );
  };

  return (
    <div className="page">
      <div className="container">
        <div className="grid-2">
        {renderWizardCard()}
        {renderSummaryCard()}
        </div>
      </div>
    </div>
  );
}
